<template>
  <table class="dd-stats mt-2">
    <thead>
      <tr>
        <th class="dd-stats-label">group</th>
        <th>n</th>
        <th>mean</th>
        <th>95% CI</th>
        <th>
          <span class="dd-wrap">{{ versusHeader }}</span>
        </th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="row in rows" :key="'gst-' + row.level">
        <td class="dd-stats-label">{{ row.level }}</td>
        <td>{{ row.n }}</td>
        <td>{{ fmt(row.mean) }}</td>
        <td>
          <span v-if="row.ciLower === null">&mdash;</span>
          <span v-else
            >{{ fmt(row.ciLower) }}&ndash;{{ fmt(row.ciUpper) }}</span
          >
        </td>
        <td :class="{ 'dd-sig': isSignificant(row) }">{{ versusRef(row) }}</td>
      </tr>
    </tbody>
  </table>
</template>

<script>
import { formatPValue, formatStat } from '@/utils/dashboard';

export default {
  name: 'GroupStatsTable',
  props: {
    // [{level, n, mean, ciLower, ciUpper}] from groupStats()
    rows: {
      type: Array,
      required: true,
    },
    referenceLevel: {
      type: String,
      default: null,
    },
    // level -> p-value against the reference; empty when unavailable
    pValues: {
      type: Map,
      default: () => new Map(),
    },
    // shown in place of a p-value when none could be computed
    unavailableLabel: {
      type: String,
      default: '—',
    },
  },
  computed: {
    // naming the reference group in the header saves explaining "ref" below
    versusHeader() {
      return this.referenceLevel ? `vs ${this.referenceLevel}` : 'vs ref';
    },
  },
  methods: {
    fmt: formatStat,
    isReference(row) {
      return row.level === this.referenceLevel;
    },
    // the reference row is blank rather than marked: its own label already
    // matches the column header, so there is nothing to compare it against
    versusRef(row) {
      if (this.isReference(row)) return '';
      if (!this.pValues.has(row.level)) return this.unavailableLabel;
      return formatPValue(this.pValues.get(row.level));
    },
    isSignificant(row) {
      if (this.isReference(row)) return false;
      const p = this.pValues.get(row.level);
      return p != null && p < 0.05;
    },
  },
};
</script>

<style scoped>
.dd-stats {
  width: 100%;
  border-collapse: collapse;
  font-family: monospace;
  font-size: 11px;
  color: #333;
}
.dd-stats th {
  text-align: right;
  vertical-align: bottom;
  font-weight: 400;
  color: #777;
  padding: 2px 6px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
.dd-stats td {
  text-align: right;
  padding: 2px 6px;
  white-space: nowrap;
}
/* level names can run long, so they wrap onto a second line rather than
   stretching the column or being clipped. Applied to the row labels too, since
   the reference level appears in both places and should read the same way. */
.dd-wrap {
  display: inline-block;
  max-width: 10em;
  white-space: normal;
  overflow-wrap: break-word;
}
.dd-stats-label {
  text-align: left !important;
  max-width: 12em;
  white-space: normal;
  overflow-wrap: break-word;
}
.dd-sig {
  font-weight: 700;
  color: #c62828;
}
</style>
